import { useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { PackThumbnail } from './PackThumbnail';
import styles from './SearchPicker.module.scss';

export interface PickerOption {
  value: string;
  label: string;
  hint?: string;
  thumbnailPath?: string | null;
}

interface SearchPickerProps {
  groupId: string;
  value: string;
  onChange: (value: string) => void;
  options: PickerOption[];
  placeholder?: string;
  showThumbnails?: boolean;
}

/** Champ texte avec liste déroulante recherchable (autocomplete) sur des options existantes. */
export function SearchPicker({ groupId, value, onChange, options, placeholder, showThumbnails }: SearchPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? options.filter((o) => o.value.toLowerCase().includes(q) || o.label.toLowerCase().includes(q))
      : options;
    return list.slice(0, 80);
  }, [options, query]);

  return (
    <div className={styles.picker}>
      <div className={styles.inputRow}>
        {showThumbnails && value && <PackThumbnail groupId={groupId} path={optionPath(options, value)} size={28} />}
        <input
          className={styles.input}
          value={open ? query : value}
          placeholder={placeholder}
          onFocus={() => {
            setQuery('');
            setOpen(true);
          }}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
          onChange={(e) => {
            setQuery(e.target.value);
            onChange(e.target.value);
            setOpen(true);
          }}
        />
        <ChevronDown size={16} className={styles.chevron} />
      </div>
      {open && filtered.length > 0 && (
        <div className={styles.menu}>
          {filtered.map((o) => (
            <button
              key={o.value}
              type="button"
              className={styles.option}
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(o.value);
                setOpen(false);
              }}
            >
              {showThumbnails && <PackThumbnail groupId={groupId} path={o.thumbnailPath} size={28} />}
              <span className={styles.optLabel}>{o.label}</span>
              {o.hint && <span className={styles.optHint}>{o.hint}</span>}
            </button>
          ))}
          {options.length > 80 && <div className={styles.more}>+ {options.length - 80} autres — affine la recherche</div>}
        </div>
      )}
    </div>
  );
}

function optionPath(options: PickerOption[], value: string): string | null {
  return options.find((o) => o.value === value)?.thumbnailPath ?? null;
}
